import { useDistributionStore } from '@/features/Distribution/store';
import { useFilesStore } from '@/features/Files/store';
import { useTariffStore } from '@/features/Tariffs/store';
import type { BaseTariff } from '@/features/Tariffs/types';
import { DISTRIBUTION_STEP } from '@/router/steps';
import type { StepConfig } from '@/router/types';
import { getRoute } from '@/router/utils/routeNames';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { VglError } from '@/services/errorHandler/vglError';
import { getCurrentSpartenConfig } from '@/utils/getCurrentSpartenConfig/getCurrentSpartenConfig';
import { useStepValidators } from '@/router/utils/useStepValidators';

const getInsuranceId = (tariff: BaseTariff, tariffId: string): string => {
  if (tariff.identifier === tariffId) {
    return tariff.gesellschaft.id;
  }

  throw new VglError(VGL_ERROR_CODE.DISTRIBUTION_REQUEST_INVALID_TARIFF, {
    additionalErrorMessage: `TariffId: ${tariffId} - Tariff: ${JSON.stringify(
      tariff,
    )}`,
  });
};

const stepConfig: StepConfig = {
  path: 'antrag/:id?',
  routeName: 'contract',

  props: {
    icon: 'file',
    label: '4 Antrag',
    isValid: () =>
      !useStepValidators().isCompanyClientBlocked() &&
      useStepValidators().tariffsResultMatchesSearch() &&
      useStepValidators().tariffsSelected(),
  },

  beforeEnter: (to, _from, next) => {
    let tariffId = to.params.id as string | undefined;
    const config = getCurrentSpartenConfig();
    const tariffStore = useTariffStore();
    const distributionStore = useDistributionStore();
    const canSkip = to.query.canSkip === 'true';

    // if we entered with specific tariff id (for example clicking
    // on the contract button on page 2 oder 3) remove all other
    // selected tariffs and make this tariff our selection and
    // recommendation.
    if (tariffId !== undefined) {
      tariffStore.selection = [tariffId];
      tariffStore.recommendation = tariffId;
    } else if (tariffStore.recommendedTariff === null) {
      // if no tariff id is given, check that we have a recommendation
      tariffStore.setRecommendation(tariffStore.selection[0]);
    }

    const distributionEnabled =
      config.distribution?.channel ?? config.distribution?.pooling;

    // if distribution is not enabled or we can skip go on to contract page
    if (distributionEnabled === undefined || !distributionEnabled || canSkip) {
      // trigger file loading before entering contract page
      useFilesStore().loadFiles(
        tariffStore.calculationId,
        tariffStore.contractTariff.identifier,
      );
      next();
      return;
    }

    if (tariffId === undefined) {
      [tariffId] = tariffStore.selection;
    }

    const insuranceId = getInsuranceId(
      tariffStore.selectedTariffs[0],
      tariffId,
    );
    const channelId = distributionStore.data[insuranceId]?.selection?.channel;

    // if no distribution channel is selected and no broker numbers are set up
    // redirect to distribution selection view
    if (channelId === null || !canSkip) {
      next({
        name: getRoute(DISTRIBUTION_STEP.SELECT),
        params: { insuranceId },
      });
    }
  },
};

export default stepConfig;
